import { NgIf } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { GridApi, ICellRendererParams, IRowNode } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';

type RowOptionsParams = {
  showEdit: boolean | Function;
  showDelete: boolean | Function;
  showCheckpoint: boolean | Function;
  showDuplicate: boolean;
  tableOptional: boolean; // show options on the last row if true
  onEdit: (rowNode: IRowNode) => void;
  onDelete: (rowNode: IRowNode) => any;
  onCheckpointClick: (rowNode: IRowNode) => any;
  onDuplicateClick: (rowNode: IRowNode) => any;
  getRowCount: () => number;
  canSubmitCheckpoints?: () => boolean;
};
@Component({
  selector: 'ciao-row-options',
  imports: [MatMenuModule, MatIcon, NgIf],
  templateUrl: './row-options.component.html',
  styleUrl: './row-options.component.less',
})
export class RowOptionsComponent implements ICellRendererAngularComp {
  constructor(private readonly toastr: ToastrService) {}
  gridApi: GridApi;
  params: ICellRendererParams & RowOptionsParams;
  rowNode: IRowNode;
  rowIndex: number;
  pinnedRow: boolean;
  moreThanOneInList: boolean = false;
  tableOptional: boolean = false;
  canSubmitCheckpoints: boolean = false;

  @ViewChild('buttonTrigger') buttonTrigger: MatMenuTrigger;

  agInit(params: ICellRendererParams & RowOptionsParams): void {
    this.params = params;
    this.gridApi = params.api;
    this.rowIndex = params?.node?.rowIndex;
    this.rowNode = params?.node;

    // determine if row is the actual pinned row and not the first row in list
    if (params.node.rowPinned === 'top' && this.rowIndex === 0) {
      this.pinnedRow = true;
    } else {
      this.pinnedRow = false;
    }
    this.refresh();
  }

  deleteRow(rowNode) {
    if (this.params.onDelete) {
      this.params.onDelete(rowNode);
      return;
    }
    this.toastr.error('There was an error saving your data', 'Error');
  }

  duplicateRow(rowNode) {
    if (this.params.onDuplicateClick) {
      this.params.onDuplicateClick(rowNode);
      return;
    }
    this.toastr.error('Something weird happened on duplicate click', 'Error');
  }

  checkpointOnRow(rowNode) {
    if (this.params.onCheckpointClick) {
      this.params.onCheckpointClick(rowNode);
      return;
    }
    this.toastr.error('Something weird happened on checkpoint click', 'Error');
  }

  editRow(rowNode) {
    if (this.params.onEdit) {
      this.params.onEdit(rowNode);
    }
    // use transactions to remove data from table
    this.toastr.warning('There was an error saving your data', 'Error');
  }

  openMenu() {
    this.buttonTrigger.openMenu();
    // Use requestAnimationFrame for better timing with menu opening
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.buttonTrigger.menu) {
          this.buttonTrigger.menu.focusFirstItem();
        }
      }, 0);
    });
    this.gridApi.stopEditing();
  }

  get noOkay() {
    return (
      this.params.data.status !== 'Missed' &&
      this.params.data.status !== 'Submitted'
    );
  }

  refresh() {
    // hide row options if only one in list
    if (this.params.getRowCount) {
      this.moreThanOneInList = this.params?.getRowCount() > 1;
    }

    this.canSubmitCheckpoints = this.params.canSubmitCheckpoints?.() ?? false;

    return true;
  }
}
